import React, { useCallback, useEffect, useRef, useState } from "react";
import apiService from "../../services/apiService";
import "../Stock/StockReceiving.css";

export default function StockReceiving() {
  const [stockEntries, setStockEntries] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [receivingKey, setReceivingKey] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const mountedRef = useRef(true);

  const loadWarehouses = useCallback(async () => {
    try {
      const data = await apiService.getWarehouses();

      if (!mountedRef.current) return;

      setWarehouses(data || []);
    } catch (err) {
      console.error("Load warehouses failed:", err);

      if (!mountedRef.current) return;

      setWarehouses([]);
    }
  }, []);

  const mergePendingItems = useCallback((newItems, oldItems, options = {}) => {
    const { preserveDrafts = true, resetPODetailID = null } = options;

    const oldRows = new Map(
      (oldItems || []).map((row) => [String(row.PODetailID), row])
    );

    return (newItems || []).map((item) => {
      const key = String(item.PODetailID);
      const old = oldRows.get(key);

      const shouldResetThisRow =
        resetPODetailID !== null &&
        String(resetPODetailID) === String(item.PODetailID);

      const backendPending = Number(item.PendingToStock || 0);

      let preservedToStock =
        preserveDrafts && !shouldResetThisRow ? Number(old?.ToStock || 0) : 0;

      if (preservedToStock > backendPending) {
        preservedToStock = backendPending;
      }

      return {
        ...item,

        ToStock: preservedToStock,

        ExpiryDate:
          preserveDrafts && !shouldResetThisRow ? old?.ExpiryDate || "" : "",

        LotNumber:
          preserveDrafts && !shouldResetThisRow ? old?.LotNumber || "" : "",

        WarehouseID:
          preserveDrafts && !shouldResetThisRow ? old?.WarehouseID || "" : "",

        item_piece: item.ItemPiece || 1,
      };
    });
  }, []);

  const loadPendingItems = useCallback(
    async (options = {}) => {
      const {
        preserveDrafts = true,
        silent = false,
        resetPODetailID = null,
      } = options;

      try {
        if (!silent) {
          setLoading(true);
        }

        const data = await apiService.getPendingStockItems();

        if (!mountedRef.current) return;

        setStockEntries((prev) =>
          mergePendingItems(data || [], prev, {
            preserveDrafts,
            resetPODetailID,
          })
        );

        setLastUpdated(new Date());
      } catch (err) {
        console.error("Load pending items failed:", err);

        if (!mountedRef.current) return;

        if (!preserveDrafts) {
          setStockEntries([]);
        }
      } finally {
        if (!silent && mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [mergePendingItems]
  );

  useEffect(() => {
    mountedRef.current = true;

    loadWarehouses();

    loadPendingItems({
      preserveDrafts: false,
      silent: false,
    });

    const timer = setInterval(() => {
      loadPendingItems({
        preserveDrafts: true,
        silent: true,
      });
    }, 30000);

    return () => {
      mountedRef.current = false;
      clearInterval(timer);
    };
  }, [loadWarehouses, loadPendingItems]);

  const updateEntry = (index, field, value) => {
    setStockEntries((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleAddToStock = async (entry, index) => {
    const toStock = Number(entry.ToStock);
    const pending = Number(entry.PendingToStock);
    const piecesPerBox = Number(entry.item_piece) || 1;

    if (!toStock || toStock <= 0) {
      return alert("Enter box quantity");
    }

    if (toStock > pending) {
      return alert(`❌ Cannot receive ${toStock}. Pending is only ${pending}.`);
    }

    if (!entry.WarehouseID) {
      return alert("Select warehouse");
    }

    const rowKey = `${entry.PODetailID}-${index}`;

    try {
      setReceivingKey(rowKey);

      await apiService.addToStock({
        items: [
          {
            ItemID: entry.ItemID,
            POID: entry.POID,
            PODetailID: entry.PODetailID,
            ToStock: toStock,
            ExpiryDate: entry.ExpiryDate || null,
            WarehouseID: entry.WarehouseID,
            LotNumber: entry.LotNumber || null,
            item_piece: piecesPerBox,
          },
        ],
      });

      alert("✅ Stock received successfully");

      await loadPendingItems({
        preserveDrafts: true,
        silent: true,
        resetPODetailID: entry.PODetailID,
      });
    } catch (err) {
      alert(err?.response?.data?.error || "❌ Failed to receive stock");
    } finally {
      setReceivingKey(null);
    }
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return "Not yet updated";

    return lastUpdated.toLocaleTimeString("en-PH", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <div className="receiving-container">
      <div className="receiving-header-card">
        <div>
          <h1>Stock Receiving</h1>
          <p className="receiving-subtitle">
            Receive pending purchase order items into warehouse stock.
          </p>
          <p className="receiving-live-status">
            Auto-sync every 30 seconds. Last updated:{" "}
            <strong>{formatLastUpdated()}</strong>
          </p>
        </div>

        <div className="receiving-header-actions">
          <button
            type="button"
            className="btn-refresh-stock"
            onClick={() =>
              loadPendingItems({
                preserveDrafts: true,
                silent: false,
              })
            }
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>

          <div className="receiving-summary-badge">
            Pending Items: <span>{stockEntries.length}</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="empty-state">
          <h3>Loading pending items...</h3>
          <p>Please wait while the receiving list is being updated.</p>
        </div>
      ) : stockEntries.length === 0 ? (
        <div className="empty-state">
          <h3>No pending items to receive</h3>
          <p>All pending purchase order items have already been received.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="po-table">
            <thead>
              <tr>
                <th>PO Number</th>
                <th>Catalog No</th>
                <th>Item Name</th>
                <th>Pending Boxes</th>
                <th>Receive Boxes</th>
                <th>Pieces / Box</th>
                <th>Lot Number</th>
                <th>Expiry Date</th>
                <th>Warehouse</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {stockEntries.map((e, i) => {
                const rowKey = `${e.PODetailID}-${i}`;
                const isReceiving = receivingKey === rowKey;

                return (
                  <tr key={rowKey}>
                    <td className="po-number">{e.PONumber}</td>

                    <td className="catalog-no">
                      {e.CatalogNo || "-"}
                    </td>

                    <td className="item-name">{e.ItemName}</td>

                    <td>
                      <span className="pending-badge">
                        {e.PendingToStock}
                      </span>
                    </td>

                    <td>
                      <input
                        type="number"
                        min="0"
                        max={e.PendingToStock}
                        value={e.ToStock}
                        disabled={isReceiving}
                        onChange={(ev) => {
                          const val = Number(ev.target.value || 0);
                          const max = Number(e.PendingToStock || 0);
                          const clamped = Math.min(val, max);

                          updateEntry(i, "ToStock", clamped);
                        }}
                        placeholder="Boxes"
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        value={e.item_piece || ""}
                        disabled
                      />
                    </td>

                    <td>
                      <input
                        type="text"
                        value={e.LotNumber}
                        disabled={isReceiving}
                        onChange={(ev) =>
                          updateEntry(i, "LotNumber", ev.target.value)
                        }
                        placeholder="Enter lot number"
                      />
                    </td>

                    <td>
                      <input
                        type="date"
                        value={e.ExpiryDate}
                        disabled={isReceiving}
                        onChange={(ev) =>
                          updateEntry(i, "ExpiryDate", ev.target.value)
                        }
                      />
                    </td>

                    <td>
                      <select
                        value={e.WarehouseID}
                        disabled={isReceiving}
                        onChange={(ev) =>
                          updateEntry(i, "WarehouseID", ev.target.value)
                        }
                      >
                        <option value="">Select warehouse</option>

                        {warehouses.map((w) => (
                          <option key={w.warehouseId} value={w.warehouseId}>
                            {w.warehouseName}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td>
                      <button
                        className="btn-add-stock"
                        disabled={
                          isReceiving ||
                          !e.ToStock ||
                          Number(e.ToStock) <= 0 ||
                          Number(e.ToStock) > Number(e.PendingToStock)
                        }
                        onClick={() => handleAddToStock(e, i)}
                      >
                        {isReceiving ? "Receiving..." : "Receive"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}